import React, { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Socket, io } from 'socket.io-client';

import {
  Box,
  Button,
  Chip,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { regions } from '@luxuryescapes/lib-regions/lib/regions';

import { noop } from '~/utils/noop';

import Spinner from '../../Common/Spinner';
import BedbankOfferBox from '../OfferListOrder/OfferBox/BedbankOfferBox';
import LEOfferBox from '../OfferListOrder/OfferBox/LEOfferBox';

interface SearchResult {
  kind: 'le' | 'bedbank';
  type: string;
  id: string;
  propertyId: string;
  packages: Array<string>;
}

interface SearchPayload {
  brand: string;
  region: string;
  naturalLanguageQuery: string;
  occupancy: ['2'];
  minOffersRequired: number;
}

interface SearchDebugEvent {
  type: 'destinationStrings' | 'searchParams';
  destinationStrings?: Array<string>;
  generatedSearchParams?: Record<string, unknown>;
}

function NaturalLanguageSearchPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [region, setRegion] = useState('AU');
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<SearchResult>>([]);
  const [isSocketConnecting, setIsSocketConnecting] = useState(true);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [destinationStrings, setDestinationStrings] = useState<Array<string>>([]);
  const [searchParams, setSearchParams] = useState<Record<string, unknown> | null>(null);
  const [offerDescriptions, setOfferDescriptions] = useState<Array<{ offerId: string; description: string }>>([]);
  const [offerValidators, setOfferValidators] = useState<Array<{ offerId: string; valid: boolean }>>([]);
  const socketRef = useRef<Socket | null>(null);

  const brand = useSelector((state: App.State) => state.tenant.brand);

  const handleRegionChange = useCallback(function (event: SelectChangeEvent) {
    setRegion(event.target.value);
  }, []);

  const handleQueryChange = useCallback(function (event: ChangeEvent<HTMLInputElement>) {
    setQuery(event.target.value);
  }, []);

  const handleSocketConnect = useCallback(function () {
    setIsSocketConnected(true);
    setIsSocketConnecting(false);
  }, []);

  const handleSocketDisconnect = useCallback(function () {
    setIsSocketConnected(false);
    setIsSocketConnecting(false);
  }, []);

  const handleSearchResponse = useCallback(function (data: { result: SearchResult }) {
    setSearchResults((prevResults) => [...prevResults, data.result]);
    setFetchingState('success');
  }, []);

  const handleDebugEvent = useCallback(function (data: SearchDebugEvent) {
    if (data?.type === 'destinationStrings' && data.destinationStrings) {
      setDestinationStrings(data.destinationStrings);
      return;
    }

    if (data?.type === 'searchParams' && data.generatedSearchParams) {
      setSearchParams(data.generatedSearchParams);
    }
  }, []);

  const handleSearchEnd = useCallback(
    function () {
      setFetchingState('success');
      if (searchResults.length === 0) {
        enqueueSnackbar('No results for your search', { variant: 'warning' });
      }
    },
    [enqueueSnackbar, searchResults.length],
  );

  const handleConnectionError = useCallback(
    function () {
      enqueueSnackbar('Failed to connect to search service', { variant: 'error' });
      setFetchingState('failed');
      handleSocketDisconnect();
    },
    [enqueueSnackbar, handleSocketDisconnect],
  );

  const handleSearchError = useCallback(
    function () {
      enqueueSnackbar('A search error occurred. Please refresh the page and try again.', { variant: 'error' });
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setFetchingState('failed');
      handleSocketDisconnect();
    },
    [enqueueSnackbar, handleSocketDisconnect],
  );

  const getOfferDescription = useCallback(
    (offerId: string) => {
      const description = offerDescriptions.find((desc) => desc.offerId === offerId);
      return description?.description || undefined;
    },
    [offerDescriptions],
  );

  const handleOfferDescriptionResponse = useCallback((payload: { offerId: string; description: string }) => {
    setOfferDescriptions((prev) => [...prev, { offerId: payload.offerId, description: payload.description }]);
  }, []);

  const handleOfferValidatorResponse = useCallback((payload: { offerId: string; valid: boolean }) => {
    setOfferValidators((prev) => [...prev, { offerId: payload.offerId, valid: payload.valid }]);
  }, []);

  useEffect(function initializeSocket() {
    setIsSocketConnecting(true);
    setIsSocketConnected(false);
    setFetchingState('idle');
    setSearchResults([]);
    setDestinationStrings([]);
    setOfferValidators([]);
    setSearchParams(null);

    const protocol = 'wss';
    const host = window.configs.API_HOST.replace(/^.*\/\//, '');
    const uri = `${protocol}://${host}`;

    const socket = io(uri, {
      transports: ['websocket'],
      path: '/api/search/stream',
    });

    socketRef.current = socket;

    socket.on('connect', handleSocketConnect);
    socket.on('disconnect', handleSocketDisconnect);
    socket.on('connect_error', handleConnectionError);
    socket.on('search:response:offer', handleSearchResponse);
    socket.on('search:response:debug', handleDebugEvent);
    socket.on('search:response:offer:description', handleOfferDescriptionResponse);
    socket.on('search:response:offer:validator', handleOfferValidatorResponse);
    socket.on('search:response:end', handleSearchEnd);
    socket.on('search:response:error', handleSearchError);

    return function cleanupSocket() {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      handleSocketDisconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearch() {
    if (!query.trim()) {
      return;
    }

    if (!isSocketConnected || !socketRef.current) {
      enqueueSnackbar('Socket not connected. Please refresh the page and try again.', { variant: 'warning' });
      return;
    }
    setOfferDescriptions([]);
    setFetchingState('loading');
    setSearchResults([]);
    setOfferValidators([]);
    setDestinationStrings([]);
    setSearchParams(null);

    const searchPayload: SearchPayload = {
      brand,
      region,
      occupancy: ['2'],
      naturalLanguageQuery: query,
      minOffersRequired: 20,
    };

    socketRef.current.emit('search:request:initial', searchPayload);
  }

  function handleFormSubmit(e: FormEvent) {
    e.preventDefault();
    handleSearch();
  }

  return (
    <Box>
      <form onSubmit={handleFormSubmit}>
        <Stack spacing={2}>
          <FormGroup>
            <Box display="flex" gap={4} alignItems="center">
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="region-select-label">Region</InputLabel>
                <Select labelId="region-select-label" label="Region" value={region} onChange={handleRegionChange}>
                  {regions['luxuryescapes'].map((region) => (
                    <MenuItem key={region.code} value={region.code}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <TextField
                  label="What are you looking for?"
                  value={query}
                  onChange={handleQueryChange}
                  placeholder="e.g. 'Show me luxury beach resorts in Bali with a pool'"
                  fullWidth
                  disabled={isSocketConnecting}
                />
              </FormControl>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSocketConnecting || fetchingState === 'loading' || !query.trim()}
                sx={{ minWidth: 120, height: 56 }}
              >
                {isSocketConnecting ? 'Connecting...' : 'Search'}
              </Button>
            </Box>
          </FormGroup>
        </Stack>
      </form>

      {(destinationStrings.length > 0 || searchParams) && (
        <Box mb={3} p={2} borderRadius={2} bgcolor="#f5f5f5" display="flex" gap={4}>
          {destinationStrings.length > 0 && (
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Destinations being searched for:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {destinationStrings.map((dest, idx) => (
                  <Chip key={dest + idx} label={dest} color="primary" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
          {searchParams && (
            <Box flex={1} minWidth={0}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Search Params
              </Typography>
              <Box
                component="pre"
                sx={{
                  background: '#f0f0f0',
                  borderRadius: 1,
                  p: 2,
                  fontSize: 13,
                  overflowX: 'auto',
                  maxHeight: 200,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {JSON.stringify(searchParams, null, 2)}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {fetchingState === 'loading' && <Spinner />}

      {fetchingState === 'success' && searchResults.length === 0 && <Box mt={4}>No offers found</Box>}

      {fetchingState === 'success' && searchResults.length > 0 && (
        <>
          {offerValidators.length >= 3 && offerValidators.every((validator) => !validator.valid) && (
            <Box mt={4} mb={3} p={3} borderRadius={2} bgcolor="#F5F5F5" border="1px solid" borderColor="#E0E0E0">
              <Typography variant="body1" color="text.primary" fontWeight={400}>
                We couldn't match your exact query, but here are our best results:
              </Typography>
            </Box>
          )}
          <Box mt={4} display="flex" flexDirection="column" gap={3}>
            {searchResults.map((offer, index) => {
              const description = getOfferDescription(offer.id);
              const descriptionLines = description ? description.split(' - ').map((line) => line.trim()) : [];

              return (
                <Box
                  key={offer.id}
                  display="flex"
                  flexDirection="column"
                  gap={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  {offer.kind === 'le' ? (
                    <LEOfferBox bk={offer.id} region={region} index={index} />
                  ) : (
                    <BedbankOfferBox bk={offer.id} region={region} index={index} onRemove={noop} />
                  )}
                  {description && (
                    <Box
                      sx={{
                        p: 2,
                        borderTop: '1px solid #e0e0e0',
                        bgcolor: '#f7f7fa',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Why this hotel matches your search:
                      </Typography>
                      <Box component="div" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                        {descriptionLines.map((line, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              display: 'block',
                              mb: idx < descriptionLines.length - 1 ? 1 : 0,
                            }}
                          >
                            {line}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
export default NaturalLanguageSearchPage;
